import { Field, Input, Text, Textarea, VStack } from '@chakra-ui/react'

type TextFieldProps = {
  label: string
  placeholder: string
  helper: string
  value: string
  /** Server or client validation copy. Replaces the helper text when set. */
  error?: string
  isRequired?: boolean
  onChange: (value: string) => void
}

type FieldBodyProps = {
  error?: string
  helper: string
}

function FieldFootnote({ error, helper }: Readonly<FieldBodyProps>) {
  if (error) {
    return <Field.ErrorText>{error}</Field.ErrorText>
  }
  return <Field.HelperText>{helper}</Field.HelperText>
}

/**
 * The Salesforce account id the operator is *creating*. Never used for the
 * compare-and-swap token: that one is displayed, not typed — see
 * `SalesforceCurrentAccountId`.
 */
export function SalesforceAccountIdField({
  label,
  placeholder,
  helper,
  value,
  error,
  onChange,
}: Readonly<TextFieldProps>) {
  return (
    <Field.Root invalid={Boolean(error)}>
      <Field.Label>{label}</Field.Label>
      <Input
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
      <FieldFootnote error={error} helper={helper} />
    </Field.Root>
  )
}

/**
 * The audited operator narrative. Required on re-point and clear, optional on
 * a first link — and required on any confirmed retry, because that sends a
 * token and the contract pairs the two.
 */
export function SalesforceReasonField({
  label,
  placeholder,
  helper,
  value,
  error,
  isRequired = false,
  onChange,
}: Readonly<TextFieldProps>) {
  return (
    <Field.Root required={isRequired} invalid={Boolean(error)}>
      <Field.Label>{label}</Field.Label>
      <Textarea
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
      <FieldFootnote error={error} helper={helper} />
    </Field.Root>
  )
}

type CurrentAccountIdProps = {
  label: string
  value?: string
}

/**
 * The stored link, read-only and never an `<Input>`: the operator names this
 * value, they never type it.
 */
export function SalesforceCurrentAccountId({
  label,
  value,
}: Readonly<CurrentAccountIdProps>) {
  return (
    <VStack align="start" gap={1}>
      <Text fontSize="xs" color="text.text-subtle">
        {label}
      </Text>
      <Text fontFamily="mono" fontSize="sm" wordBreak="break-all">
        {value}
      </Text>
    </VStack>
  )
}
